import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  clearDesignCataloguePresence,
  listDesignCatalogueEditors,
  pingDesignCataloguePresence,
  type DesignCatalogueEditor,
} from '../lib/designCatalogues';
import { formatDisplayPersonName } from '../lib/formatPersonName';

const HEARTBEAT_MS = 12_000;
const POLL_MS = 8_000;

/** Soft presence: heartbeat while editing a catalogue; returns other active editors. */
export function useDesignCataloguePresence(projectId: string | null) {
  const { authUser, profile } = useAuth();
  const [others, setOthers] = useState<DesignCatalogueEditor[]>([]);

  const email = authUser?.email ?? profile?.email ?? null;
  const displayName =
    formatDisplayPersonName(profile?.display_name?.trim() || profile?.username) ||
    email ||
    'Someone';
  const authId = authUser?.id ?? null;

  useEffect(() => {
    if (!projectId || !authId) {
      setOthers([]);
      return undefined;
    }

    let cancelled = false;

    const refresh = async () => {
      try {
        const rows = await listDesignCatalogueEditors(projectId);
        if (cancelled) return;
        setOthers(rows.filter((row) => row.auth_id !== authId));
      } catch {
        /* presence table may not be migrated yet */
      }
    };

    const ping = async () => {
      try {
        await pingDesignCataloguePresence({
          projectId,
          authId,
          email,
          displayName,
        });
      } catch {
        /* ignore */
      }
      await refresh();
    };

    void ping();
    const beatId = window.setInterval(() => void ping(), HEARTBEAT_MS);
    const pollId = window.setInterval(() => void refresh(), POLL_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') void ping();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(beatId);
      window.clearInterval(pollId);
      document.removeEventListener('visibilitychange', onVisible);
      void clearDesignCataloguePresence(projectId, authId).catch(() => {
        /* ignore */
      });
      setOthers([]);
    };
  }, [projectId, authId, email, displayName]);

  return others;
}

export function formatDesignEditorsLabel(editors: DesignCatalogueEditor[]): string {
  if (editors.length === 0) return '';
  const names = editors.map((e) => e.display_name || e.email || 'Someone');
  if (names.length === 1) return `${names[0]} is also editing`;
  if (names.length === 2) return `${names[0]} and ${names[1]} are also editing`;
  return `${names[0]} and ${names.length - 1} others are also editing`;
}
